import {
  AllDataDevice,
  DeviceWithoutLegacyPassword,
  DeviceWithoutLegacyPasswordList2,
} from "../generated/types";
import { I18nKey } from "../i18n/i18n";
import { fetchWithToken } from "../utils/fetchWithToken";
import { isAdminAccount } from "../utils/roles";
import { apiFetchJson, apiFetchRoute, buildPath } from "./apiFetchRoute";

/**
 * Produits visibles par l'utilisateur courant.
 *
 * Deux routes selon le rôle (même couple que les bâtiments, cf. `api/building.ts`) :
 *  - **admin/psc** → `getAdminDevicesAlldata` : tous les produits de la plateforme, déjà
 *    enrichis (entreprise, bâtiment, élevage, comptes liés) ;
 *  - **les autres** → `getDevices` (`GetUserDevices` côté DATA) : uniquement les produits
 *    auxquels le compte est lié via `LinkAccountDevice`.
 *
 * La route scopée renvoie un `Device` + le NOM de son entreprise (`company_name`), résolu
 * côté DATA via USERS. On l'enveloppe dans la forme `AllDataDevice` attendue par l'écran ;
 * bâtiment et élevage restent absents (prévus plus tard). Les composants d'affichage
 * utilisent déjà l'optional chaining avec un repli « - », donc les colonnes non alimentées
 * se vident proprement au lieu de casser.
 *
 * `?? []` : les deux routes répondent **204 No Content** quand il n'y a aucun produit ;
 * `apiFetchJson` renvoie alors `undefined`, et un `.filter` direct lèverait un TypeError
 * avalé par le catch — ce qui affichait une erreur là où il n'y a qu'une liste vide.
 */
export const fetchDevices = async (): Promise<{
  devices: AllDataDevice[];
  error: string | null;
}> => {
  try {
    if (!isAdminAccount()) {
      // `GET /devices` renvoie désormais chaque produit enrichi du nom de son entreprise
      // (`company_name`) : indispensable pour un installateur, dont la liste mélange les
      // produits de plusieurs clients. On le remonte dans `company` pour que la colonne
      // existante du tableau l'affiche sans modification.
      const scoped = (await apiFetchJson("getDevices", undefined)) as
        | (DeviceWithoutLegacyPassword & { company_name?: string })[]
        | undefined;

      const devices = (scoped ?? [])
        .filter((device) => !!device && !!device.type)
        .map(
          ({ company_name, ...device }) =>
            ({
              device,
              company: company_name ? { company_name } : undefined,
            }) as unknown as AllDataDevice
        );
      return { devices, error: null };
    }

    const data = await apiFetchJson("getAdminDevicesAlldata", undefined);

    // Vérifier la structure des données
    const validDevices = (data ?? []).filter((device) => {
      const isValid = device && device.device && device.device.type;
      if (!isValid) {
        console.warn("Appareil invalide trouvé:", device);
      }
      return isValid;
    });

    return { devices: validDevices, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { devices: [], error: "Impossible de charger les produits" };
  }
};

export const fetchDeviceAll = async (): Promise<{
  devices: DeviceWithoutLegacyPassword[];
  error: string | null;
}> => {
  try {
    const data = await apiFetchJson("getAdminDevices", undefined);

    // Vérifier la structure des données
    // `?? []` : cette route répond **204 No Content** quand il n'y a aucun device
    // (cf. VM_DATA handlers/device.go) ; apiFetchJson renvoie alors `undefined` et
    // `data.filter` lèverait un TypeError avalé par le catch.
    const validDevices = (data ?? []).filter((device) => {
      const isValid = device && device.type;
      if (!isValid) {
        console.warn("Appareil invalide trouvé:", device);
      }
      return isValid;
    });

    return { devices: validDevices, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { devices: [], error: "Impossible de charger les produits" };
  }
};

export const fetchDeviceMe = async (): Promise<{
  devices: DeviceWithoutLegacyPasswordList2;
  error: string | null;
}> => {
  try {
    const data = await apiFetchJson("getDevices", undefined);

    // `?? []` : GetUserDevices répond **204 No Content** quand l'utilisateur n'a aucun device
    // lié (cf. VM_DATA handlers/device.go), et apiFetchJson renvoie alors `undefined`. Sans ce
    // garde, `data.filter` lève un TypeError avalé par le catch ci-dessous : le vrai « aucun
    // produit » devenait indistinguable d'une panne réseau.
    const validDevices = (data ?? []).filter((device) => {
      const isValid = device && device.type;
      if (!isValid) {
        console.warn("Appareil invalide trouvé:", device);
      }
      return isValid;
    });

    return { devices: validDevices, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { devices: [], error: "Impossible de charger les produits" };
  }
};

/**
 * Régulateurs que l'utilisateur courant a le droit de voir. C'est la source du sélecteur du
 * widget régulateur.
 *
 * Deux règles d'accès distinctes, décidées le 2026-07-30 :
 *  - **admin / psc** : TOUS les produits (`getAdminDevices`) ;
 *  - **tout autre type** : uniquement ceux liés à SON compte dans `LinkAccountDevice`
 *    (`getDevices` → GetUserDevices, qui fait un INNER JOIN sur cette table).
 *
 * Conséquence assumée : deux utilisateurs d'une même entreprise ne voient pas forcément les
 * mêmes régulateurs, et un produit sans ligne de liaison est invisible pour un non-admin — même
 * si son bâtiment et ses mesures existent. C'est une règle métier, pas un défaut de données.
 */
export const fetchAccessibleDevices = async (): Promise<{
  devices: DeviceWithoutLegacyPassword[];
  error: string | null;
}> => (isAdminAccount() ? fetchDeviceAll() : fetchDeviceMe());

/**
 * Produits « voisins » d'un régulateur, pour le sélecteur et le mode split de la vue à
 * distance.
 *
 * Deux routes selon le rôle :
 *  - **admin/psc** → `getAdminDevicesByCompanyUuid` : tous les produits de l'entreprise
 *    propriétaire du régulateur (route sous AdminMiddleware) ;
 *  - **les autres** → `getDevices` : les produits AUXQUELS ILS ONT ACCÈS (possession
 *    directe ou partage via partenariat), filtrés côté serveur.
 *
 * Un éleveur n'a pas à voir tous les produits de son entreprise ici — seulement les siens.
 * L'ancienne version appelait la route admin pour tout le monde, d'où un **403
 * AdminMiddleware** dès qu'un éleveur ouvrait la vue à distance.
 */
export const fetchDevicesByCompanyUuid = async (
  uuid: string
): Promise<{
  devices: DeviceWithoutLegacyPasswordList2;
  error: string | null;
}> => {
  try {
    const data = isAdminAccount()
      ? await apiFetchJson("getAdminDevicesByCompanyUuid", undefined, {
          params: { uuid },
        })
      : await apiFetchJson("getDevices", undefined);

    // Vérifier la structure des données
    // `?? []` : cette route répond **204 No Content** quand il n'y a aucun device
    // (cf. VM_DATA handlers/device.go) ; apiFetchJson renvoie alors `undefined` et
    // `data.filter` lèverait un TypeError avalé par le catch.
    const validDevices = (data ?? []).filter((device) => {
      const isValid = device && device.type;
      if (!isValid) {
        console.warn("Appareil invalide trouvé:", device);
      }
      return isValid;
    });

    return { devices: validDevices, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { devices: [], error: "Impossible de charger les produits" };
  }
};

export const fetchDevice = async (
  uuid: string
): Promise<{
  device: DeviceWithoutLegacyPassword | undefined;
  error: string | null;
}> => {
  try {
    const device = await apiFetchJson("getDevicesUuidUuid", undefined, {
      params: { uuid },
    });
    // Vérifier la structure des données
    const isValid = device && device.type;
    if (!isValid) {
      console.warn("Appareil invalide trouvé:", device);
    }

    return { device: device, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { device: undefined, error: "Impossible de charger les produits" };
  }
};

/**
 * Clé de traduction du motif d'échec d'une connexion à distance, selon le statut renvoyé par
 * VM_DATA (`/remote_vnc/{uuid}` ou `/link/{uuid}/{version}`). Le 401 est géré en amont par
 * `fetchWithToken`.
 *   - 204 : box injoignable (pas connectée au VPN, ou IP en base portée par une autre box) ;
 *   - 422 : infos de connexion manquantes en base (IP VPN, port ou mot de passe VNC, MAC).
 */
// Clés construites ici et affichées ailleurs avec t(error) : ces commentaires les gardent
// dans les traductions (parse_translation supprime les clés qu'il ne voit pas écrites).
// i18n.t("remote-view-reason-unavailable")
// i18n.t("remote-view-reason-denied")
// i18n.t("remote-view-reason-not-found")
// i18n.t("remote-view-reason-failed")
export const remoteErrorKey = (status: number): string => {
  switch (status) {
    case 204:
    case 422:
      return "remote-view-reason-unavailable";
    case 403:
      return "remote-view-reason-denied";
    case 404:
      return "remote-view-reason-not-found";
    default:
      return "remote-view-reason-failed";
  }
};

/**
 * Lien de connexion à distance d'une Serenity. `error` est une clé de traduction
 * (voir `remoteErrorKey`), à afficher avec `t(error)`.
 */
export const getUrlSerenity = async (
  device: DeviceWithoutLegacyPassword
): Promise<{
  url: string;
  error: string | null;
}> => {
  try {
    const response = await fetchWithToken(
      `${process.env.REACT_APP_URL_SERVER_DATA}${buildPath(
        "/link/{uuid}/{version}",
        { uuid: device.uuid, version: device.version }
      )}`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json",
        },
      }
    );
    // Le statut d'abord : un 204 n'a pas de corps, `json()` échouerait.
    if (response.status !== 200) {
      return { url: "", error: remoteErrorKey(response.status) };
    }
    return { url: await response.json(), error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { url: "", error: remoteErrorKey(0) };
  }
};

/**
 * Lien noVNC d'un produit legacy (avibox, avitouch, labelbox, vigebox). `error` est une clé
 * de traduction (voir `remoteErrorKey`), à afficher avec `t(error)`.
 */
export const getUrlVigebox = async (
  device: DeviceWithoutLegacyPassword
): Promise<{
  url: string;
  error: string | null;
}> => {
  try {
    const response = await fetchWithToken(
      `${process.env.REACT_APP_URL_SERVER_DATA}${buildPath(
        "/remote_vnc/{uuid}",
        { uuid: device.uuid }
      )}`,
      {
        method: "GET",
        headers: {
          "Content-type": "application/json",
        },
      }
    );
    if (response.status !== 200) {
      return { url: "", error: remoteErrorKey(response.status) };
    }
    const data = await response.json();
    return { url: data.novnc_url, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { url: "", error: remoteErrorKey(0) };
  }
};

export const getDevicesNotAssociatedToBuilding = async (
  id_company: number
): Promise<{
  devices: DeviceWithoutLegacyPasswordList2;
  error: string | null;
}> => {
  try {
    const devices = await apiFetchJson(
      "getDevicesBuildingNotassociatedId_company",
      undefined,
      {
        params: { id_company },
      }
    );
    // Vérifier la structure des données
    const validDevices = devices.filter((device) => {
      const isValid = device && device.type;
      if (!isValid) {
        console.warn("Appareil invalide trouvé:", device);
      }
      return isValid;
    });

    return { devices: validDevices, error: null };
  } catch (err) {
    console.error("Erreur:", err);
    return { devices: [], error: "Impossible de charger les produits" };
  }
};

// Fonction pour supprimer le produit
export const deleteDevice = async (uuid: string) => {
  const response = await apiFetchRoute("deleteDevicesUuid", undefined, {
    params: { uuid },
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la suppression du produit");
  }

  return response.json();
};

/**
 * Associe un produit à une entreprise.
 *
 * Qui peut appeler, et vers quelle entreprise (le backend tranche, cf. VM_DATA
 * `UpdateDeviceAssociateToCompany`) :
 *  - **admin / psc** : n'importe quelle entreprise ;
 *  - **breeder** : la sienne — `id_company` envoyé ici est IGNORÉ et écrasé ;
 *  - **installer / group** : une entreprise avec laquelle ils sont en partenariat accepté ;
 *  - **leurs collaborateurs** : idem, à condition que leur chef leur ait accordé le droit
 *    « peut associer » sur CE partenariat.
 *
 * Le produit doit être libre (`id_company = -1` en base) et le triplet
 * uuid + type + mot de passe doit correspondre : c'est la preuve de possession du matériel.
 *
 * Un 404 est volontairement indistinct (produit inconnu, déjà associé, ou mauvais mot de
 * passe) : le backend ne veut pas offrir d'oracle d'énumération.
 *
 * `id_building` : obligatoire pour les régulateurs legacy hors vigebox (cf.
 * `BUILDING_REQUIRED_TYPES`), facultatif sinon — envoyer 0 vaut « aucun bâtiment ». Le
 * backend refuse (403) un bâtiment qui n'appartient pas à l'entreprise cible.
 */
export const associateDeviceToCompany = async (data: {
  id_company: number;
  uuid: string;
  type: string;
  password_legacy: string;
  id_building: number;
}): Promise<{ success: boolean; error: string | null }> => {
  try {
    const response = await apiFetchRoute(
      "putDevicesAssociatedevicetouser",
      data as never
    );

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error:
            "Aucun produit disponible ne correspond. Vérifiez le numéro de série, le type et le mot de passe — le produit est peut-être déjà associé.",
        };
      }
      if (response.status === 403) {
        return {
          success: false,
          // Deux causes possibles : entreprise interdite, ou bâtiment qui ne lui
          // appartient pas. Le backend ne les distingue pas dans sa réponse.
          error:
            "Association refusée : vérifiez que le bâtiment appartient bien à cette entreprise.",
        };
      }
      if (response.status === 400) {
        return {
          success: false,
          error:
            "Ce type de produit doit être installé dans un bâtiment. Sélectionnez-en un.",
        };
      }
      return { success: false, error: "Une erreur est survenue" };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error("Erreur lors de l'association du produit:", err);
    return { success: false, error: "Une erreur est survenue" };
  }
};

/**
 * Rattache à un bâtiment un produit déjà associé à l'entreprise (et le retire de son
 * éventuel bâtiment précédent). Rend la clé du message d'erreur, vide en cas de succès.
 */
export const attachDeviceToBuilding = async (
  deviceId: number,
  buildingId: number
): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute(
      "putDevicesId_deviceBuildingId_building",
      undefined,
      { params: { id_device: deviceId, id_building: buildingId } }
    );
    if (response.ok) return "";
    // i18n.t("device-attach-building-failed")
    // i18n.t("device-attach-building-forbidden")
    return response.status === 403
      ? "device-attach-building-forbidden"
      : "device-attach-building-failed";
  } catch (error) {
    console.error("Erreur lors du rattachement au bâtiment:", error);
    return "device-attach-building-failed";
  }
};
